import { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { RootState } from '@/reducers'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { adminEnrollmentHistoryCollection } from '@/reducers/admin/adminEnrollmentHistory'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  loadingText: {
    marginLeft: theme.spacing(2),
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginRight: theme.spacing(2),
  },
  chipContainer: {
    display: 'flex',
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  enrollmentDetails: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  stateColumn: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      marginBottom: theme.spacing(0.5),
    },
  },
  monospaceText: {
    fontSize: '0.75rem',
    fontFamily: 'monospace',
  },
  reasonText: {
    maxWidth: 300,
    wordWrap: 'break-word',
  },
  reasonChips: {
    marginTop: theme.spacing(0.5),
    '& > *': {
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
  },
}))

type EnrollmentHistoryProps = {
  userEmail?: string
  profileID?: string
}

type EnrollmentHistoryEntry = {
  id: number
  created: string
  modified: string
  enrollment_type: number
  enrollment_id: number
  enrollment_type_name: string
  enrollment_info: {
    id: number
    created?: string
    program_name?: string
    device_type: string
    device_id?: number
  } | null
  current_status: string
  current_enrolled: boolean
  current_participation_status?: string
  current_eligibility_status?: string
  new_status: string
  new_enrolled?: boolean
  new_participation_status?: string
  new_eligibility_status?: string
  reason: string
  reasons: string[]
  source: string
  requirement_reasoning: string[]
}

// Group enrollment history by enrollment
function groupEnrollmentHistory(
  historyEntries: EnrollmentHistoryEntry[],
): Record<string, EnrollmentHistoryEntry[]> {
  const groups: Record<string, EnrollmentHistoryEntry[]> = {}

  historyEntries.forEach((entry) => {
    const key = `${entry.enrollment_type_name}_${entry.enrollment_id}`
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(entry)
  })

  // Sort each group by creation date (newest first)
  Object.keys(groups).forEach((key) => {
    groups[key].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
    )
  })

  return groups
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString()
}

function getStatusChipColor(
  status?: string,
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' {
  if (!status) return 'default'

  switch (status.toLowerCase()) {
    case 'enrolled':
    case 'active':
    case 'eligible':
      return 'success'
    case 'unenrolled':
    case 'inactive':
    case 'ineligible':
      return 'error'
    case 'pending':
      return 'warning'
    case 'completed':
      return 'primary'
    default:
      return 'default'
  }
}

function getDeviceTypeLabel(deviceType: string, deviceId?: number): string {
  const typeLabel = deviceType.charAt(0).toUpperCase() + deviceType.slice(1)
  return deviceId ? `${typeLabel} #${deviceId}` : typeLabel
}

export default function EnrollmentHistory({
  userEmail,
  profileID,
}: EnrollmentHistoryProps) {
  const classes = useStyles()
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set())

  const {
    data: enrollmentHistoryData,
    isLoading,
    status,
  } = adminEnrollmentHistoryCollection.useFetch({
    params: profileID ? { profile_id: profileID } : undefined,
    require: !!profileID,
    refetchOnChange: true,
  })

  const groupedHistory = useMemo(() => {
    if (!enrollmentHistoryData) {
      return {}
    }
    // The resource collection returns the data as an array
    const historyArray = Array.isArray(enrollmentHistoryData)
      ? enrollmentHistoryData
      : []
    return groupEnrollmentHistory(historyArray)
  }, [enrollmentHistoryData])

  const handleAccordionToggle = useCallback((panelId: string) => {
    setExpandedPanels((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(panelId)) {
        newSet.delete(panelId)
      } else {
        newSet.add(panelId)
      }
      return newSet
    })
  }, [])

  if (!profileID) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" gutterBottom>
          Enrollment History
        </Typography>
        <Typography color="textSecondary">
          Please select a user to view their enrollment history.
        </Typography>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" gutterBottom>
          Enrollment History
        </Typography>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body2" className={classes.loadingText}>
            Loading enrollment history...
          </Typography>
        </Box>
      </div>
    )
  }

  const historyGroups = Object.entries(groupedHistory)

  if (historyGroups.length === 0) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" gutterBottom>
          Enrollment History
        </Typography>
        <Typography color="textSecondary">
          No enrollment history found for this user.
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Enrollment History
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        Track enrollment state changes over time. Each enrollment shows its
        status change history with timestamps and reasons.
      </Typography>

      <div>
        {historyGroups.map(([groupKey, historyEntries]) => {
          const firstEntry = historyEntries[0]
          const enrollmentInfo = firstEntry.enrollment_info
          const isExpanded = expandedPanels.has(groupKey)

          return (
            <Accordion
              key={groupKey}
              expanded={isExpanded}
              onChange={() => handleAccordionToggle(groupKey)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box className={classes.accordionSummaryContent}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" component="div">
                      {enrollmentInfo?.program_name || 'Unknown Program'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {getDeviceTypeLabel(
                        enrollmentInfo?.device_type || 'unknown',
                        enrollmentInfo?.device_id,
                      )}
                      {' • '}
                      {historyEntries.length} state change
                      {historyEntries.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip
                      size="small"
                      label={firstEntry.enrollment_type_name || 'Unknown Type'}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box>
                  {/* Basic enrollment info */}
                  <Box className={classes.enrollmentDetails}>
                    <Typography variant="subtitle2" gutterBottom>
                      Enrollment Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Enrollment ID:</strong> {firstEntry.enrollment_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {firstEntry.enrollment_type_name}
                    </Typography>
                    {enrollmentInfo?.created && (
                      <Typography variant="body2">
                        <strong>Created:</strong>{' '}
                        {formatDateTime(enrollmentInfo.created)}
                      </Typography>
                    )}
                  </Box>

                  {/* History table */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Timestamp</strong>
                          </TableCell>
                          <TableCell>
                            <strong>From State</strong>
                          </TableCell>
                          <TableCell>
                            <strong>To State</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Reason</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Source</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDateTime(entry.created)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box className={classes.stateColumn}>
                                <Typography
                                  variant="body2"
                                  className={classes.monospaceText}
                                >
                                  <strong>Status:</strong>{' '}
                                  {entry.current_status}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className={classes.monospaceText}
                                >
                                  <strong>Enrolled:</strong>{' '}
                                  {entry.current_enrolled ? 'true' : 'false'}
                                </Typography>
                                {entry.current_participation_status && (
                                  <Typography
                                    variant="body2"
                                    className={classes.monospaceText}
                                  >
                                    <strong>Participation:</strong>{' '}
                                    {entry.current_participation_status}
                                  </Typography>
                                )}
                                {entry.current_eligibility_status && (
                                  <Typography
                                    variant="body2"
                                    className={classes.monospaceText}
                                  >
                                    <strong>Eligibility:</strong>{' '}
                                    {entry.current_eligibility_status}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box className={classes.stateColumn}>
                                <Typography
                                  variant="body2"
                                  className={classes.monospaceText}
                                >
                                  <strong>Status:</strong> {entry.new_status}
                                </Typography>
                                {entry.new_enrolled !== undefined && (
                                  <Typography
                                    variant="body2"
                                    className={classes.monospaceText}
                                  >
                                    <strong>Enrolled:</strong>{' '}
                                    {entry.new_enrolled ? 'true' : 'false'}
                                  </Typography>
                                )}
                                {entry.new_participation_status && (
                                  <Typography
                                    variant="body2"
                                    className={classes.monospaceText}
                                  >
                                    <strong>Participation:</strong>{' '}
                                    {entry.new_participation_status}
                                  </Typography>
                                )}
                                {entry.new_eligibility_status && (
                                  <Typography
                                    variant="body2"
                                    className={classes.monospaceText}
                                  >
                                    <strong>Eligibility:</strong>{' '}
                                    {entry.new_eligibility_status}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={classes.reasonText}
                              >
                                {entry.reason}
                              </Typography>
                              {entry.reasons && entry.reasons.length > 0 && (
                                <Box className={classes.reasonChips}>
                                  {entry.reasons.map((reason, idx) => (
                                    <Chip
                                      key={idx}
                                      size="small"
                                      label={reason}
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={entry.source || 'Unknown'}
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </div>
    </div>
  )
}
